import { Link } from 'react-router-dom';
import { Building2, MessageCircle, Mail, Phone, Clock } from 'lucide-react';

export default function Support() {
  return (
    <div className="min-h-full bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#36CCD8] rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">Vecindus</span>
            </Link>
            <Link 
              to="/login" 
              className="px-4 py-2 text-[#36CCD8] font-semibold hover:text-[#2DB8C4] transition-colors"
            >
              Iniciar sesión
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Centro de Soporte</h1>
          <p className="text-lg text-gray-600">
            Estamos aquí para ayudarte. Elige la opción de contacto que prefieras.
          </p>
        </div>

        {/* Contact Options */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-slate-50 rounded-2xl p-6 text-center">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-7 h-7 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Chat en vivo</h3>
            <p className="text-sm text-gray-500 mb-4">
              Chatea con nuestro equipo de soporte en tiempo real
            </p>
            <button className="w-full py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors">
              Iniciar chat
            </button>
          </div>

          <div className="bg-slate-50 rounded-2xl p-6 text-center">
            <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Mail className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Correo electrónico</h3>
            <p className="text-sm text-gray-500 mb-4">
              Envíanos un correo y te responderemos en menos de 24 horas
            </p>
            <a 
              href="mailto:info@vecindus.com" 
              className="inline-block w-full py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            >
              info@vecindus.com
            </a>
          </div>

          <div className="bg-slate-50 rounded-2xl p-6 text-center">
            <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Phone className="w-7 h-7 text-orange-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Teléfono</h3>
            <p className="text-sm text-gray-500 mb-4">
              Habla directamente con nuestro equipo de soporte
            </p>
            <a 
              href="tel:+528001234567" 
              className="inline-block w-full py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors"
            >
              800 123 4567
            </a>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Preguntas Frecuentes</h2>
          
          <div className="space-y-4">
            <details className="bg-slate-50 rounded-xl p-4">
              <summary className="font-medium text-gray-900 cursor-pointer">
                ¿Cómo registro una cuenta en Vecindus?
              </summary>
              <p className="text-gray-600 mt-2 pt-2 border-t border-slate-200">
                Haz clic en "Regístrate" en la página principal, ingresa tus datos y el código de tu condominio 
                para unirte a tu comunidad.
              </p>
            </details>

            <details className="bg-slate-50 rounded-xl p-4">
              <summary className="font-medium text-gray-900 cursor-pointer">
                ¿Cómo realizo el pago de mis cuotas?
              </summary>
              <p className="text-gray-600 mt-2 pt-2 border-t border-slate-200">
                Inicia sesión, ve a la sección de "Pagos" y selecciona el método de pago preferido. 
                Aceptamos tarjetas, transferencias y pago en efectivo en puntos de recolección.
              </p>
            </details>

            <details className="bg-slate-50 rounded-xl p-4">
              <summary className="font-medium text-gray-900 cursor-pointer">
                ¿Cómo registro a un visitante?
              </summary>
              <p className="text-gray-600 mt-2 pt-2 border-t border-slate-200">
                Desde la app o web, ve a "Visitantes" y registra los datos de tu visitante. 
                Recibirá un código QR para acceder el día de su visita.
              </p>
            </details>

            <details className="bg-slate-50 rounded-xl p-4">
              <summary className="font-medium text-gray-900 cursor-pointer">
                ¿Qué hago si olvidé mi contraseña?
              </summary>
              <p className="text-gray-600 mt-2 pt-2 border-t border-slate-200">
                Haz clic en "¿Olvidaste tu contraseña?" en la página de inicio de sesión e ingresa tu correo 
                electrónico para recibir un enlace de recuperación.
              </p>
            </details>
          </div>
        </div>

        {/* Hours */}
        <div className="bg-[#36CCD8]/5 rounded-2xl p-6 flex items-center gap-4">
          <Clock className="w-10 h-10 text-[#36CCD8] flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-gray-900">Horario de atención</h3>
            <p className="text-gray-600">
              Lunes a Viernes: 9:00 AM - 6:00 PM (hora local)<br />
              Sábados: 9:00 AM - 2:00 PM<br />
              Domingos: Cerrado
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
